import { Component, AfterViewInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { RailPeruService } from '../../services/rail-peru.service';

type Fuente = 'WISE' | 'INVESTING' | 'VITA_PUBLIC';

/**
 * Vista TRM USD/PEN: mercado (Investing) vs tasa pública de Vita + spread.
 * Espejo de `RailColombiaComponent` sobre el módulo `trmpen` del backend.
 *
 * Diferencias respecto de la vista COP, todas por la escala del par (~3,38):
 *  - las tasas y el spread se muestran con 4 decimales (con 2, un paso de
 *    cuantización serían ~0,3 %, más que el spread que se quiere leer);
 *  - la marca de hora local viene en `fechaHoraPeru` (no `fechaHoraColombia`).
 */
@Component({
  selector: 'app-rail-peru',
  templateUrl: './rail-peru.component.html',
  styleUrls: ['./rail-peru.component.sass'],
})
export class RailPeruComponent implements AfterViewInit {
  /**
   * Última fila de cada fuente. `WISE` es la referencia de mercado vigente;
   * `INVESTING` quedó como serie histórica (sin datos desde el 2026-07-16,
   * no se llega al sitio desde producción).
   */
  tasas: {
    WISE: any | null;
    INVESTING: any | null;
    VITA_PUBLIC: any | null;
  } = {
    WISE: null,
    INVESTING: null,
    VITA_PUBLIC: null,
  };
  refreshing = false;

  /** Filtro de fuente del historial ('' = todas). */
  filtroFuente: '' | Fuente = '';

  displayedColumns: string[] = [
    'fechaUtc',
    'horaPeru',
    'tasa',
    'variacion',
    'fuente',
  ];
  dataSource: MatTableDataSource<any> | undefined;

  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

  constructor(
    private railService: RailPeruService,
    private spinner: SpinnerService,
  ) {}

  ngAfterViewInit(): void {
    // El paginator ya existe acá → la carga lo puede asignar al dataSource.
    setTimeout(() => this.load());
  }

  async load(): Promise<void> {
    const ref = this.spinner.start();
    try {
      this.tasas = (await this.railService.getLatestAll()) || {
        WISE: null,
        INVESTING: null,
        VITA_PUBLIC: null,
      };
      const rows = (await this.railService.getHistory(200)) || [];
      // Siempre del último registrado al primero (más nuevo arriba).
      rows.sort(
        (a, b) =>
          new Date(b.fechaHoraUtc).getTime() -
          new Date(a.fechaHoraUtc).getTime(),
      );
      this.dataSource = new MatTableDataSource(rows);
      this.dataSource.filterPredicate = (row, filter) =>
        !filter || row.fuente === filter;
      this.dataSource.filter = this.filtroFuente;
      if (this.paginator) this.dataSource.paginator = this.paginator;
    } finally {
      this.spinner.stop(ref);
    }
  }

  /** Aplica el filtro de fuente sobre el historial ya cargado (cliente). */
  aplicarFiltro(): void {
    if (this.dataSource) this.dataSource.filter = this.filtroFuente;
  }

  /**
   * Referencia de mercado vigente: **Wise**.
   *
   * Era Investing, pero desde la IP de producción no se llega a investing.com
   * y esa serie no recibe un dato desde el 2026-07-16. El spread de esta
   * pantalla se calculaba contra ese valor congelado, así que mostraba un
   * número sin significado. No se le deja fallback a INVESTING a propósito:
   * las dos fuentes no cotizan igual y alternarlas movería el spread sin que
   * el mercado se mueva.
   */
  get mercado(): any | null {
    return this.tasas?.WISE ?? null;
  }

  get vita(): any | null {
    return this.tasas?.VITA_PUBLIC ?? null;
  }

  /** Spread en PEN: cuántos PEN MENOS da Vita por USD vs el mercado. */
  get spreadPen(): number | null {
    const m = Number(this.mercado?.tasa);
    const v = Number(this.vita?.tasa);
    if (!Number.isFinite(m) || !Number.isFinite(v)) return null;
    return m - v;
  }

  /** Spread en %: descuento de la tasa pública de Vita bajo el mercado. */
  get spreadPct(): number | null {
    const m = Number(this.mercado?.tasa);
    const v = Number(this.vita?.tasa);
    if (!Number.isFinite(m) || !Number.isFinite(v) || m === 0) return null;
    return ((m - v) / m) * 100;
  }

  /** Etiqueta legible de la fuente (el backend guarda el código interno). */
  fuenteLabel(fuente: string): string {
    switch (fuente) {
      case 'WISE':
        return 'Wise (mercado)';
      case 'INVESTING':
        return 'Investing.com (histórico)';
      case 'VITA_PUBLIC':
        return 'Vita (tasa pública)';
      default:
        return fuente || '—';
    }
  }

  /** Fuerza una consulta inmediata al backend (ambas fuentes) y recarga. */
  async forzarConsulta(): Promise<void> {
    this.refreshing = true;
    try {
      await this.railService.forceRefresh();
      await this.load();
    } finally {
      this.refreshing = false;
    }
  }
}
